import { lazy, Suspense } from "react";
import { Navigate, Outlet, createBrowserRouter, useLocation } from "react-router";
import { AppShell } from "./components/AppShell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Spinner } from "./components/ui";
import { ApiError, selectCompany, selectedCompanyId } from "./lib/api";
import { MeProvider, useCan, useMe, useMeQuery } from "./lib/session";
import { AppsPage } from "./pages/AppsPage";
import { AcceptInvitePage } from "./pages/auth/AcceptInvitePage";
import { LoginPage } from "./pages/auth/LoginPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/auth/PasswordPages";
import { DashboardPage } from "./pages/DashboardPage";
const AccountPage = lazy(() => import("./pages/AccountPage").then((m) => ({ default: m.AccountPage })));
const DirectoryPage = lazy(() => import("./pages/DirectoryPage").then((m) => ({ default: m.DirectoryPage })));
const MembersPage = lazy(() => import("./pages/admin/MembersPage").then((m) => ({ default: m.MembersPage })));
const AuditPage = lazy(() => import("./pages/admin/AuditPage").then((m) => ({ default: m.AuditPage })));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage").then((m) => ({ default: m.PortfolioPage })));
const AppRegistryPage = lazy(() => import("./pages/AppRegistryPage").then((m) => ({ default: m.AppRegistryPage })));
const SetupPage = lazy(() => import("./pages/SetupPage").then((m) => ({ default: m.SetupPage })));
const ApplicationsPage = lazy(() => import("./pages/admin/ApplicationsPage").then((m) => ({ default: m.ApplicationsPage })));
import { AccessDeniedPage, ConsentPage, MfaRequiredPage, NotFoundPage } from "./pages/MiscPages";

function FullPageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner label="Loading Galeway" />
    </div>
  );
}

/** Gate for signed-in routes. Routing is UX only — the API enforces everything. */
function RequireSession({ allowMfaEnrollment = false }: { allowMfaEnrollment?: boolean }) {
  const me = useMeQuery();
  const location = useLocation();
  if (me.isLoading) return <FullPageSpinner />;
  if (me.isError) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    }
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center px-6 text-center">
        <p className="text-sm font-semibold">We couldn't load your account</p>
        <p className="mt-1 max-w-sm text-sm text-muted">{me.error.message}</p>
        <a href="/login" className="mt-4 text-sm font-medium text-accent">
          Return to sign in
        </a>
      </div>
    );
  }
  const data = me.data!;
  // Pin the company being viewed so adding/joining companies never silently switches it.
  if (!selectedCompanyId()) selectCompany(data.company.id);
  if (data.mfaEnrollmentRequired && !allowMfaEnrollment) return <Navigate to="/setup-mfa" replace />;
  if (!data.mfaEnrollmentRequired && allowMfaEnrollment) return <Navigate to="/" replace />;
  return (
    <MeProvider me={data}>
      <Outlet />
    </MeProvider>
  );
}

function RequirePlatformAdmin() {
  const me = useMe();
  return me.isPlatformAdmin ? <Outlet /> : <NotFoundPage />;
}

function RequirePermission({ permission }: { permission: string }) {
  const can = useCan();
  return can(permission) ? <Outlet /> : <NotFoundPage />;
}

function Root() {
  return (
    <ErrorBoundary>
      <Outlet />
    </ErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      { path: "/accept-invite", element: <AcceptInvitePage /> },
      { path: "/access-denied", element: <AccessDeniedPage /> },
      { path: "/consent", element: <ConsentPage /> },
      {
        path: "/setup",
        element: (
          <Suspense fallback={<FullPageSpinner />}>
            <SetupPage />
          </Suspense>
        ),
      },
      { element: <RequireSession allowMfaEnrollment />, children: [{ path: "/setup-mfa", element: <MfaRequiredPage /> }] },
      {
        element: <RequireSession />,
        children: [
          {
            element: <AppShell />,
            // Admin, directory and account screens load on demand.
            children: [
              { index: true, element: <DashboardPage /> },
              { path: "/apps", element: <AppsPage /> },
              { path: "/account", element: <AccountPage /> },
              { element: <RequirePermission permission="employees.read" />, children: [{ path: "/directory", element: <DirectoryPage /> }] },
              { element: <RequirePermission permission="users.read" />, children: [{ path: "/admin/members", element: <MembersPage /> }] },
              { element: <RequirePermission permission="apps.read" />, children: [{ path: "/admin/applications", element: <ApplicationsPage /> }] },
              { element: <RequirePermission permission="audit.read" />, children: [{ path: "/admin/audit", element: <AuditPage /> }] },
              {
                element: <RequirePlatformAdmin />,
                children: [
                  { path: "/portfolio", element: <PortfolioPage /> },
                  { path: "/portfolio/applications", element: <AppRegistryPage /> },
                ],
              },
              { path: "*", element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
